/**
 * DiffPopout Component
 *
 * Modal popout for viewing file diffs in agent sessions.
 *
 * @module components/session/DiffPopout
 */

import { memo, useState, useCallback, useEffect, lazy, Suspense } from 'react';
import { X, Download, FileText, Loader2, Columns, AlignJustify, Eye, BookOpen, Code, Pencil, Save } from 'lucide-react';

// Lazy load the diff viewer for bundle optimization (~50KB)
const DiffViewer = lazy(() => import('./DiffViewer.jsx'));

/**
 * Check if file is a Markdown file
 */
const isMarkdownFile = (filePath) => {
  return filePath?.toLowerCase().endsWith('.md');
};

/**
 * View mode toggle (Split/Unified/File)
 */
const ViewModeToggle = memo(function ViewModeToggle({ viewMode, onChange }) {
  return (
    <div className="flex items-center bg-gray-700 rounded-md p-0.5">
      <button
        onClick={() => onChange('split')}
        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors
                   ${viewMode === 'split' ? 'bg-gray-600 text-gray-100' : 'text-gray-400 hover:text-gray-300'}`}
        title="Split diff view"
      >
        <Columns className="w-3.5 h-3.5" />
        <span>Split</span>
      </button>
      <button
        onClick={() => onChange('unified')}
        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors
                   ${viewMode === 'unified' ? 'bg-gray-600 text-gray-100' : 'text-gray-400 hover:text-gray-300'}`}
        title="Unified diff view"
      >
        <AlignJustify className="w-3.5 h-3.5" />
        <span>Unified</span>
      </button>
      <button
        onClick={() => onChange('file')}
        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors
                   ${viewMode === 'file' ? 'bg-gray-600 text-gray-100' : 'text-gray-400 hover:text-gray-300'}`}
        title="View full file"
      >
        <Eye className="w-3.5 h-3.5" />
        <span>File</span>
      </button>
    </div>
  );
});

/**
 * Markdown mode toggle (Preview/Source/Edit) - only shown for .md files in file view
 */
const MarkdownModeToggle = memo(function MarkdownModeToggle({ markdownMode, onChange, onEditClick }) {
  return (
    <div className="flex items-center bg-gray-700 rounded-md p-0.5 ml-2">
      <button
        onClick={() => onChange('preview')}
        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors
                   ${markdownMode === 'preview' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}
        title="Rendered Markdown preview"
      >
        <BookOpen className="w-3.5 h-3.5" />
        <span>Preview</span>
      </button>
      <button
        onClick={() => onChange('source')}
        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors
                   ${markdownMode === 'source' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}
        title="View source code"
      >
        <Code className="w-3.5 h-3.5" />
        <span>Source</span>
      </button>
      <button
        onClick={onEditClick}
        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors
                   ${markdownMode === 'edit' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-gray-300'}`}
        title="Edit markdown"
      >
        <Pencil className="w-3.5 h-3.5" />
        <span>Edit</span>
      </button>
    </div>
  );
});

/**
 * Generate unified diff string from old and new content
 */
function generateUnifiedDiff(filePath, oldContent, newContent) {
  const oldLines = (oldContent || '').split('\n');
  const newLines = (newContent || '').split('\n');

  let diff = `--- a/${filePath}\n+++ b/${filePath}\n`;

  // Simple line-by-line diff (not optimal but functional)
  const maxLen = Math.max(oldLines.length, newLines.length);
  let hunkStart = -1;
  let hunkLines = [];

  for (let i = 0; i < maxLen; i++) {
    const oldLine = oldLines[i];
    const newLine = newLines[i];

    if (oldLine !== newLine) {
      if (hunkStart === -1) hunkStart = i;
      if (oldLine !== undefined) hunkLines.push(`-${oldLine}`);
      if (newLine !== undefined) hunkLines.push(`+${newLine}`);
    } else if (hunkStart !== -1) {
      // Context line
      if (newLine !== undefined) hunkLines.push(` ${newLine}`);
    }
  }

  if (hunkLines.length > 0) {
    diff += `@@ -${hunkStart + 1},${oldLines.length} +${hunkStart + 1},${newLines.length} @@\n`;
    diff += hunkLines.join('\n');
  }

  return diff;
}

/**
 * Download content as file
 */
function downloadFile(content, filename) {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * DiffPopout - Modal container for file diff viewing
 */
export const DiffPopout = memo(function DiffPopout({
  file,
  onClose,
  isLoading = false,
  agentId, // Required for save functionality
}) {
  const [viewMode, setViewMode] = useState('split');
  const [markdownMode, setMarkdownMode] = useState('source'); // 'preview' | 'source' | 'edit'
  const [editContent, setEditContent] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Auto-detect: Switch to preview mode for .md files
  useEffect(() => {
    if (file && isMarkdownFile(file.path)) {
      setMarkdownMode('preview');
      // Auto-switch to file view for better markdown experience
      if (viewMode !== 'file') {
        setViewMode('file');
      }
      // Initialize edit content
      setEditContent(file.content || '');
      setIsDirty(false);
      setSaveError(null);
    } else {
      setMarkdownMode('source');
    }
  }, [file?.path]); // Only trigger on file change

  // Handle edit mode activation
  const handleEditClick = useCallback(() => {
    setMarkdownMode('edit');
    setEditContent(file?.content || '');
    setIsDirty(false);
    setSaveError(null);
  }, [file?.content]);

  // Handle content changes in edit mode
  const handleEditChange = useCallback((value) => {
    setEditContent(value);
    setIsDirty(value !== file?.content);
  }, [file?.content]);

  // Save handler
  const handleSave = useCallback(async () => {
    if (!file || !agentId || !isDirty) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      const filePath = file.path.startsWith('/') ? file.path : `/${file.path}`;
      const response = await fetch(`/api/agents/${agentId}/files${filePath}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: editContent }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Save failed (${response.status})`);
      }

      setIsDirty(false);
      // Update file content in memory for preview
      if (file) {
        file.content = editContent;
      }
    } catch (error) {
      console.error('Save error:', error);
      setSaveError(error.message);
    } finally {
      setIsSaving(false);
    }
  }, [file, agentId, editContent, isDirty]);

  // Handle escape key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Download handlers
  const handleDownloadOriginal = useCallback(() => {
    if (file?.previousContent) {
      const filename = file.path.split('/').pop();
      downloadFile(file.previousContent, `${filename}.original`);
    }
  }, [file]);

  const handleDownloadDiff = useCallback(() => {
    if (file?.previousContent && file?.content) {
      const filename = file.path.split('/').pop();
      const diff = generateUnifiedDiff(file.path, file.previousContent, file.content);
      downloadFile(diff, `${filename}.diff`);
    }
  }, [file]);

  if (!file) return null;

  const hasOriginal = !!file.previousContent;

  // Dynamic width: wide for split view, narrower for unified/file
  const modalWidth = viewMode === 'split' ? 'max-w-[95vw]' : 'max-w-5xl';

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className={`bg-gray-800 rounded-lg w-full ${modalWidth} max-h-[90vh] flex flex-col shadow-2xl border border-gray-700 transition-all duration-200`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <span className="text-gray-100 font-medium truncate max-w-md" title={file.path}>
              {file.shortPath || file.fileName || file.path}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded ${
              file.type === 'edit'
                ? 'bg-orange-500/20 text-orange-400'
                : 'bg-green-500/20 text-green-400'
            }`}>
              {file.type === 'edit' ? 'Modified' : 'Created'}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />

            {/* Markdown mode toggle - only show for .md files in file view */}
            {viewMode === 'file' && isMarkdownFile(file.path) && (
              <MarkdownModeToggle
                markdownMode={markdownMode}
                onChange={setMarkdownMode}
                onEditClick={handleEditClick}
              />
            )}

            {/* Save button - only show when dirty */}
            {isDirty && (
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-1 px-2 py-1.5 bg-green-600 hover:bg-green-500 disabled:bg-green-800 disabled:cursor-not-allowed rounded-md text-xs text-white transition-colors"
                title="Save changes"
              >
                <Save className="w-3.5 h-3.5" />
                <span>{isSaving ? 'Saving...' : 'Save'}</span>
              </button>
            )}

            {/* Download buttons */}
            {hasOriginal && (
              <>
                <button
                  onClick={handleDownloadOriginal}
                  className="flex items-center gap-1 px-2 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-md text-xs text-gray-300 transition-colors"
                  title="Download original file"
                >
                  <FileText className="w-3.5 h-3.5" />
                  <span>Original</span>
                </button>
                <button
                  onClick={handleDownloadDiff}
                  className="flex items-center gap-1 px-2 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-md text-xs text-gray-300 transition-colors"
                  title="Download diff file"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Diff</span>
                </button>
              </>
            )}

            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-700 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
              title="Close (Esc)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
            </div>
          ) : (
            <Suspense fallback={
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
              </div>
            }>
              <DiffViewer
                oldContent={file.previousContent}
                newContent={file.content}
                filePath={file.path}
                language={file.language}
                viewMode={viewMode}
                markdownMode={markdownMode}
                isMarkdownFile={isMarkdownFile(file.path)}
                editContent={editContent}
                onEditChange={handleEditChange}
              />
            </Suspense>
          )}
        </div>

        {/* Footer with file info */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-700 text-xs text-gray-500">
          <div className="flex items-center gap-4">
            <span>{file.language || 'plaintext'}</span>
            {saveError && (
              <span className="text-red-400">Error: {saveError}</span>
            )}
            {isDirty && !saveError && (
              <span className="text-amber-400">Unsaved changes</span>
            )}
          </div>
          <span>{(markdownMode === 'edit' ? editContent : file.content)?.split('\n').length || 0} lines</span>
        </div>
      </div>
    </div>
  );
});

export default DiffPopout;
